import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Typography,
  Tag,
  Button,
  Switch,
  Input,
  Select,
  Row,
  Col,
  Space,
  Avatar,
  Statistic,
  Tooltip,
  Badge,
} from "antd";
import {
  BankOutlined,
  SyncOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  MobileOutlined,
  ApiOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import useDebounce from "../hooks/useDebounce";
import { ShowToast, Severty } from "../helper/toast";

const { Title, Text } = Typography;
const { Option } = Select;

const BankConnectionManager = () => {
  const { request } = useRequest();

  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [banks, setBanks] = useState([]);
  const [stats, setStats] = useState({
    total_banks: 0,
    enabled_banks: 0,
    app_redirect_enabled: 0,
    official_connections: 0,
    classic_connections: 0,
  });

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [connectionType, setConnectionType] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // ----------------------------------------------------
  // Fetch Banks
  // ----------------------------------------------------
  const fetchBanks = useCallback(
    (page = 1, limit = 20) => {
      setLoading(true);

      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      if (debouncedSearch.trim()) params.append("search", debouncedSearch.trim());
      if (connectionType) params.append("connection_type", connectionType);
      if (statusFilter !== "") params.append("is_enabled", statusFilter);

      request({
        url: `/api/admin/bank-connections?${params.toString()}`,
        method: "GET",
        onSuccess: (res) => {
          setLoading(false);
          const data = res?.data || {};
          setBanks(data?.results || []);
          if (data?.stats) setStats(data.stats);
          if (data?.pagination) {
            setPagination((prev) => ({
              ...prev,
              current: data.pagination.page,
              pageSize: data.pagination.limit,
              total: data.pagination.total,
            }));
          }
        },
        onError: (err) => {
          setLoading(false);
          ShowToast(err?.message || "Failed to load bank connections", Severty.ERROR);
        },
      });
    },
    [request, debouncedSearch, connectionType, statusFilter]
  );

  useEffect(() => {
    fetchBanks(1, pagination.pageSize);
  }, [fetchBanks]);

  // ----------------------------------------------------
  // Toggle Switch Handler
  // ----------------------------------------------------
  const handleToggle = (bankId, field, value) => {
    // Optimistic UI update
    setBanks((prev) =>
      prev.map((item) => (item.id === bankId ? { ...item, [field]: value } : item))
    );

    request({
      url: `/api/admin/bank-connections/${bankId}`,
      method: "PATCH",
      data: { [field]: value },
      onSuccess: (res) => {
        ShowToast(res?.message || "Bank connection updated successfully", Severty.SUCCESS);
        fetchBanks(pagination.current, pagination.pageSize);
      },
      onError: (err) => {
        // Revert on error
        setBanks((prev) =>
          prev.map((item) => (item.id === bankId ? { ...item, [field]: !value } : item))
        );
        ShowToast(err?.message || "Failed to update setting", Severty.ERROR);
      },
    });
  };

  // ----------------------------------------------------
  // Manual Akahu Sync
  // ----------------------------------------------------
  const handleSyncWithAkahu = () => {
    setSyncing(true);
    request({
      url: "/api/admin/bank-connections/sync",
      method: "POST",
      onSuccess: (res) => {
        setSyncing(false);
        ShowToast(res?.message || "Bank connections synchronized with Akahu!", Severty.SUCCESS);
        fetchBanks(1, pagination.pageSize);
      },
      onError: (err) => {
        setSyncing(false);
        ShowToast(err?.message || "Failed to sync with Akahu", Severty.ERROR);
      },
    });
  };

  // ----------------------------------------------------
  // Table Columns
  // ----------------------------------------------------
  const columns = [
    {
      title: "Bank",
      key: "bank",
      render: (_, record) => (
        <Space size="middle">
          <Avatar
            shape="square"
            size={44}
            src={record.logo}
            icon={<BankOutlined />}
            style={{ backgroundColor: "#f0f2f5" }}
          />
          <div>
            <Text strong style={{ fontSize: "15px", display: "block" }}>
              {record.name}
            </Text>
            <Text type="secondary" style={{ fontSize: "12px", fontFamily: "monospace" }}>
              ID: {record.conn_id}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Connection Type",
      dataIndex: "connection_type",
      key: "connection_type",
      render: (type) =>
        type === "official" ? (
          <Tag color="cyan" style={{ fontWeight: 600, padding: "3px 10px", borderRadius: "12px" }}>
            Official API
          </Tag>
        ) : (
          <Tag color="orange" style={{ fontWeight: 600, padding: "3px 10px", borderRadius: "12px" }}>
            Classic
          </Tag>
        ),
    },
    {
      title: "Akahu Status",
      key: "akahu_status",
      render: (_, record) =>
        record.new_connections_enabled ? (
          <Badge status="success" text={<Text style={{ color: "#52c41a" }}>Enabled by Akahu</Text>} />
        ) : (
          <Badge status="error" text={<Text type="secondary">Disabled by Akahu</Text>} />
        ),
    },
    {
      title: (
        <Space>
          <span>Available for Connection</span>
          <Tooltip title="Controls whether users see this bank in the mobile app picker">
            <InfoCircleOutlined style={{ color: "#1890ff" }} />
          </Tooltip>
        </Space>
      ),
      key: "is_enabled",
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Switch
            checked={record.is_enabled}
            onChange={(checked) => handleToggle(record.id, "is_enabled", checked)}
            checkedChildren="Visible"
            unCheckedChildren="Hidden"
          />
          <Text type="secondary" style={{ fontSize: "11px" }}>
            {record.is_enabled ? "Active in mobile picker" : "Hidden from users"}
          </Text>
        </Space>
      ),
    },
    {
      title: (
        <Space>
          <span>Redirect to Mobile App</span>
          <Tooltip title="Controls whether the mobile app launches the bank's app for biometric authorization (ANZ, ASB, Westpac) or falls back to web portal">
            <InfoCircleOutlined style={{ color: "#1890ff" }} />
          </Tooltip>
        </Space>
      ),
      key: "supports_app_redirect",
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Switch
            checked={record.supports_app_redirect}
            onChange={(checked) => handleToggle(record.id, "supports_app_redirect", checked)}
            checkedChildren="Bank App"
            unCheckedChildren="Web Portal"
          />
          <Text type="secondary" style={{ fontSize: "11px" }}>
            {record.supports_app_redirect ? "Launches Bank App" : "Web Auth Portal"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Mode / Details",
      key: "mode",
      render: (_, record) => (
        <div>
          {record.mode && (
            <Tag color="blue" style={{ fontSize: "11px" }}>
              Mode: {record.mode}
            </Tag>
          )}
          {record.classic_id && (
            <div style={{ fontSize: "11px", color: "#8c8c8c", marginTop: 4 }}>
              Classic: {record.classic_id}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: "16px 24px" }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            Bank Connections Management
          </Title>
          <Text type="secondary">
            Manage supported banks, in-app picker visibility, and biometric bank app handoff settings.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<SyncOutlined spin={syncing} />}
          loading={syncing}
          onClick={handleSyncWithAkahu}
          style={{ height: 40, borderRadius: 8, fontWeight: 600 }}
        >
          Sync with Akahu
        </Button>
      </div>

      {/* Metrics Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
            <Statistic
              title="Total Supported Banks"
              value={stats.total_banks}
              prefix={<BankOutlined style={{ color: "#1890ff", marginRight: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
            <Statistic
              title="Active in Mobile App"
              value={stats.enabled_banks}
              valueStyle={{ color: "#52c41a" }}
              prefix={<CheckCircleOutlined style={{ marginRight: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
            <Statistic
              title="Bank App Biometrics Enabled"
              value={stats.app_redirect_enabled}
              valueStyle={{ color: "#722ed1" }}
              prefix={<MobileOutlined style={{ marginRight: 8 }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
            <Statistic
              title="Official vs Classic"
              value={`${stats.official_connections} / ${stats.classic_connections}`}
              prefix={<ApiOutlined style={{ color: "#fa8c16", marginRight: 8 }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filter and Table Card */}
      <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 4px 14px rgba(0,0,0,0.05)" }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }} justify="space-between" align="middle">
          <Col xs={24} sm={10} md={8}>
            <Input
              placeholder="Search bank name or ID..."
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </Col>
          <Col xs={24} sm={14} md={10}>
            <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
              <Select
                placeholder="Connection Type"
                value={connectionType}
                onChange={(val) => setConnectionType(val)}
                style={{ width: 150 }}
                allowClear
              >
                <Option value="">All Types</Option>
                <Option value="official">Official API</Option>
                <Option value="classic">Classic</Option>
              </Select>
              <Select
                placeholder="Mobile Status"
                value={statusFilter}
                onChange={(val) => setStatusFilter(val)}
                style={{ width: 150 }}
                allowClear
              >
                <Option value="">All Statuses</Option>
                <Option value="true">Visible</Option>
                <Option value="false">Hidden</Option>
              </Select>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={banks}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            onChange: (page, pageSize) => fetchBanks(page, pageSize),
          }}
        />
      </Card>
    </div>
  );
};

export default BankConnectionManager;
